import { Fragment, useRef, type ElementType, type ReactNode } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import { useOnClickOutside } from "usehooks-ts";

import {
  Listbox as HeadlessUIListbox,
  ListboxButton as HeadlessUIListboxButton,
  ListboxOption as HeadlessUIListboxOption,
  ListboxOptions as HeadlessUIListboxOptions,
  Transition,
  type ListboxOptionProps,
} from "@headlessui/react";

const menuPositionClasses = {
  "top-left": "top end",
  "top-right": "top start",
  "bottom-left": "bottom end",
  "bottom-right": "bottom start",
} as const;

interface ContextMenuProps<T> {
  menuButton: ReactNode;
  className?: string;
  menuClassName?: string;
  children: ReactNode;
  menuPosition?: keyof typeof menuPositionClasses;
  menuPositionClass?: string;
  value: T;
  setValue: (v: T) => void;
}

interface MultiContextMenuProps<T> {
  menuButton: ReactNode;
  className?: string;
  menuClassName?: string;
  children: ReactNode;
  menuPosition?: keyof typeof menuPositionClasses;
  menuPositionClass?: string;
  value: T;
  setValue: (v: T) => void;
  controlled: {
    open?: boolean;
    setOpen?: (open: boolean) => void;
  };
}

export function ListBox<T>({
  className,
  menuButton,
  menuClassName,
  children,
  menuPosition = "bottom-left",
  menuPositionClass,
  value,
  setValue,
}: ContextMenuProps<T>) {
  return (
    <HeadlessUIListbox
      value={value}
      onChange={setValue}
      as="div"
      className={clsx(className, "relative")}
    >
      {menuButton}
      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <HeadlessUIListboxOptions
          anchor={menuPositionClasses[menuPosition]}
          className={twMerge(
            "z-[9] rounded-md bg-white text-left text-sm elevation-1 pb-1",
            menuClassName,
            menuPositionClass,
          )}
        >
          {children}
        </HeadlessUIListboxOptions>
      </Transition>
    </HeadlessUIListbox>
  );
}

export function MultiListBox<M>({
  className,
  menuButton,
  menuClassName,
  children,
  menuPosition = "bottom-left",
  menuPositionClass,
  value,
  setValue,
  controlled,
}: MultiContextMenuProps<M[]>) {
  const { setOpen, open } = controlled ?? {};

  const ref = useRef(null);
  useOnClickOutside(ref, () => setOpen?.(false));

  return (
    <HeadlessUIListbox
      multiple
      value={value}
      onChange={setValue}
      as="div"
      className={clsx(className, "relative")}
    >
      {menuButton}
      {controlled ? (
        open && (
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            ref={ref}
            show={open}
          >
            <HeadlessUIListboxOptions
              anchor={menuPositionClasses[menuPosition]}
              static
              className={twMerge(
                menuClassName,
                "z-[9] rounded-md bg-white text-left text-sm elevation-1 ",
                menuPositionClass,
              )}
            >
              {children}
            </HeadlessUIListboxOptions>
          </Transition>
        )
      ) : (
        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <HeadlessUIListboxOptions
            anchor={menuPositionClasses[menuPosition]}
            className={twMerge(
              menuClassName,
              "z-[9] rounded-md bg-white text-left text-sm elevation-1 ",
              menuPositionClass,
            )}
          >
            {children}
          </HeadlessUIListboxOptions>
        </Transition>
      )}
    </HeadlessUIListbox>
  );
}

ListBox.Option = function <T extends ElementType, V>({
  children,
  activeClassname = "bg-cosmic-4",
  ...props
}: ListboxOptionProps<T, V> & {
  children: ReactNode;
  className?: string;
  activeClassname?: string;
}) {
  const Component = props.as || Fragment;

  return (
    <HeadlessUIListboxOption value={props.value}>
      {({ focus }) => {
        return (
          <Component
            {...props}
            className={clsx(props.className, { [activeClassname]: focus })}
          >
            {children}
          </Component>
        );
      }}
    </HeadlessUIListboxOption>
  );
};

ListBox.Button = HeadlessUIListboxButton;

MultiListBox.Option = ListBox.Option;
MultiListBox.Button = ListBox.Button;
